import { request } from "utils/request";

// Solo prices come from the backend price API, which answers from the same
// settings checkout uses. This constant is what the dialog shows when that
// API cannot be read. It has the same shape as the API body and goes through
// the same formatter, so a later release that moves the fallback to the new
// price changes only these numbers.
export const SOLO_PRICING_FALLBACK = Object.freeze({
  currency: "usd",
  tiers: {
    Solo: {
      monthly: { amount_cents: 2900, trial_days: 7 },
      yearly: { amount_cents: 29000, trial_days: 30 },
    },
  },
});

export const SOLO_PRICING_TIMEOUT_MS = 5000;

const isPositiveInt = (value) => Number.isInteger(value) && value > 0;
const isNonNegativeInt = (value) => Number.isInteger(value) && value >= 0;

const isValidInterval = (interval) =>
  Boolean(interval) &&
  isPositiveInt(interval.amount_cents) &&
  isNonNegativeInt(interval.trial_days);

/**
 * Returns the body when it is a complete, valid Solo price set, otherwise
 * null. All or nothing: one bad field rejects the whole body.
 */
export const parseSoloPricing = (body) => {
  if (!body || typeof body !== "object") return null;
  if (body.currency !== "usd") return null;
  const solo = body.tiers?.Solo;
  if (
    !solo ||
    !isValidInterval(solo.monthly) ||
    !isValidInterval(solo.yearly)
  ) {
    return null;
  }
  return body;
};

/**
 * Whole dollars show without decimals ($79, $1,290); anything else shows two
 * decimals ($65.83, $79.50).
 */
export const formatUsdCents = (cents) => {
  const dollars = cents / 100;
  if (cents % 100 === 0) {
    return `$${dollars.toLocaleString("en-US")}`;
  }
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Turns a price body into the strings and numbers the dialog shows.
 * Callers pass an already validated body.
 */
export const buildSoloPricingView = (body) => {
  const { monthly, yearly } = body.tiers.Solo;
  const savePercent = Math.round(
    (1 - yearly.amount_cents / (monthly.amount_cents * 12)) * 100
  );
  return {
    monthly: {
      price: formatUsdCents(monthly.amount_cents),
      trialDays: monthly.trial_days,
    },
    yearly: {
      total: formatUsdCents(yearly.amount_cents),
      perMonth: formatUsdCents(Math.round(yearly.amount_cents / 12)),
      trialDays: yearly.trial_days,
    },
    savePercent: savePercent > 0 ? savePercent : null,
  };
};

/**
 * Reads the Solo prices. Always resolves with a complete, valid price body:
 * the API body when it is usable, otherwise SOLO_PRICING_FALLBACK. A network
 * error, a non-2xx answer, a body that is not valid, an abort, and a
 * timeout all give the whole fallback, never a mix.
 *
 * The timeout is a race against a timer because `request` returns a promise
 * that never settles on a 401 (it is force-logging the user out). The timer
 * is cleared once the race ends, and an answer that lands after the timeout
 * is dropped. Cancelling through `signal` also ends the race at once, so the
 * timer never outlives a closed dialog.
 *
 * @param {{signal?: AbortSignal, timeoutMs?: number}} [options] `signal`
 *   cancels the request from outside (the dialog closing).
 */
export const fetchSoloPricing = async ({
  signal,
  timeoutMs = SOLO_PRICING_TIMEOUT_MS,
} = {}) => {
  const controller = new AbortController();
  // One rejecting promise for both ways to stop early (timeout, caller
  // cancel). Rejecting it also aborts the request. It settles the race even
  // when `request` never does (the 401 force-logout path), so `finally`
  // always clears the timer.
  let stop;
  const stopped = new Promise((_, reject) => {
    stop = (reason) => {
      controller.abort();
      reject(new Error(reason));
    };
  });
  const onCancel = () => stop("Solo pricing request cancelled");
  const timer = setTimeout(
    () => stop("Solo pricing request timed out"),
    timeoutMs
  );
  if (signal?.aborted) onCancel();
  else signal?.addEventListener("abort", onCancel);

  try {
    const body = await Promise.race([
      request(`${process.env.REACT_APP_API_URL}/payments/plans/`, {
        signal: controller.signal,
      }),
      stopped,
    ]);
    return parseSoloPricing(body) || SOLO_PRICING_FALLBACK;
  } catch {
    return SOLO_PRICING_FALLBACK;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCancel);
  }
};
